import React from 'react';
import { useTranslation } from 'react-i18next';
import { Undo2, Wand2 } from 'lucide-react';
import Tooltip from 'components/ui/tooltip';
import { cn } from 'lib/utils';

interface Props {
  /** Absent when the importer had read nothing usable, so there is no earlier version to return to. */
  onRevert?: () => void;
}

/**
 * Marks a question the AI re-read from the file. The author can check it, edit
 * it like any other, or put back exactly what the importer read.
 */
export const ImportAiAdjustedBadge: React.FC<Props> = ({ onRevert }) => {
  const { t } = useTranslation();

  return (
    <span className="inline-flex items-center gap-1">
      <Tooltip
        content={t('import_ai_adjusted_hint')}
        side="top"
        className="w-64 max-w-[calc(100vw-2rem)] whitespace-normal px-3.5 py-2 text-center text-[13px] leading-relaxed"
      >
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded border border-border bg-background px-1.5 py-0.5',
            'text-[11px] font-medium text-foreground/80',
          )}
        >
          <Wand2 className="h-3 w-3" aria-hidden />
          {t('import_ai_adjusted')}
        </span>
      </Tooltip>
      {onRevert && (
        <Tooltip content={t('import_ai_revert')} side="top">
          <button
            type="button"
            aria-label={t('import_ai_revert')}
            onClick={(event) => {
              event.stopPropagation();
              onRevert();
            }}
            className={cn(
              'inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors',
              'hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring',
            )}
          >
            <Undo2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        </Tooltip>
      )}
    </span>
  );
};

export default ImportAiAdjustedBadge;
